import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { NightLine } from "@/components/sales/NightLine";
import { CategoryRules } from "@/components/sales/CategoryRules";
import { UndoBar, useUndoBar } from "@/components/sales/UndoBar";
import { Loader2, Save } from "lucide-react";

// THE RULES TAB (only with "Set sales rules"): his thresholds, each change logged with who and when, with Undo.
// A change takes effect the next time the list is worked out (tonight at 02:30, or "Work it out now").
const RULES = [
  ["aging_months", "Aging", "months on the shelf before it is recommended"],
  ["keep_per_number", "Keep per part number", "parts kept of each part number — the rest are recommended"],
  ["asked_times", "Asked for", "searches in the last 90 days"],
  ["high_value_over", "High value", "dollars of internal value — above this it is recommended"],
];

export function RulesTab() {
  const [res, setRes] = useState(null);
  const bar = useUndoBar();
  const load = () => api.get("/sales/rules").then((r) => setRes(r.data)).catch(() => setRes({ error: true }));
  useEffect(() => { load(); }, []);
  const save = async (change) => {
    try { const { data } = await api.put("/sales/rules", change); bar.push(data); load(); return true; }   // the Undo bar says it
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };
  if (!res) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;
  if (res.error) return <p data-testid="sales-rules-error" className="text-sm text-slate-600">Couldn't read the rules. Open the tab again.</p>;
  return (
    <div data-testid="sales-rules" className="space-y-3">
      <NightLine night={res.night} canWorkOut testid="sales-rules-night" onDone={load} />
      {RULES.map(([k, label, unit]) => <RuleRow key={k} k={k} label={label} unit={unit} value={res.rules[k]} save={save}
        note={k === "aging_months" ? agingNote(res.aging) : null} />)}
      <CategoryRules rules={res.rules.categories} categories={res.categories} kinds={res.category_kinds} save={save} />
      <UndoBar bar={bar} after={load} testid="sales-rules-undo-bar" />
    </div>
  );
}

function agingNote(a) {
  if (a.on) return "Switched on — the yard has 6 months of records.";
  return `Switched off until the yard has 6 months of records${a.starts ? ` — it switches itself on ${new Date(a.starts).toLocaleDateString([], { dateStyle: "medium" })}` : ""}.`;
}

function RuleRow({ k, label, unit, value, save, note }) {
  const [v, setV] = useState(String(value));
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(String(value)), [value]);
  const changed = v !== String(value) && v.trim() !== "";
  return (
    <div data-testid={`sales-rule-${k}`} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[200px]">
        <div className="font-bold text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{unit}</div>
        {note && <div data-testid={`sales-rule-${k}-note`} className="text-xs text-amber-800 mt-1">{note}</div>}
      </div>
      <Input data-testid={`sales-rule-${k}-input`} inputMode="numeric" value={v} onChange={(e) => setV(e.target.value.replace(/[^0-9]/g, ""))} className="h-11 w-28 text-right font-mono" />
      <button type="button" data-testid={`sales-rule-${k}-save`} disabled={busy || !changed}
        onClick={async () => { setBusy(true); await save({ [k]: Number(v) }); setBusy(false); }}
        className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg bg-[#1F1F1F] hover:bg-black text-white text-sm font-bold disabled:opacity-40">
        <Save className="w-4 h-4" />Save {changed ? v : ""}</button>
    </div>
  );
}
